import React, { useState } from 'react';
import { X, Send, ShieldAlert, CheckCircle2, MessageSquare, Loader2, Sparkles } from 'lucide-react';
import { concernsAPI } from '../../services/api';

export function SubmitConcernModal({ user, onClose, onSuccess }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('General Concern');
  const [contactNumber, setContactNumber] = useState('');
  const [contactNumberError, setContactNumberError] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState(null);

  const handleContactNumberChange = (e) => {
    // Only allow digits
    const digits = e.target.value.replace(/\D/g, '');
    setContactNumber(digits);
    if (digits.length > 0 && digits.length !== 11) {
      setContactNumberError('Contact number must be exactly 11 digits.');
    } else {
      setContactNumberError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !details.trim()) {
      alert('Please provide both a subject and details for your concern.');
      return;
    }
    if (contactNumber.length > 0 && contactNumber.length !== 11) {
      setContactNumberError('Contact number must be exactly 11 digits.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await concernsAPI.submitConcern({
        userId: user?.id || user?.username || 'anonymous',
        userName: user?.fullname || user?.fullName || user?.username || 'Valued User',
        userRole: user?.role || 'user',
        userEmail: user?.email || '',
        contactNumber,
        subject,
        category,
        details
      });

      if (res.success) {
        setSubmittedMessage(res.message || 'Thank you for your feedback or concern, we will be right with you!');
        if (onSuccess) onSuccess();
      } else {
        alert(res.message || 'Failed to submit concern. Please try again.');
        setIsSubmitting(false);
      }
    } catch (err) {
      alert(err.message || 'An error occurred while submitting your concern.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl animate-slide-up flex flex-col border border-amber-100">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 p-5 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2.5 rounded-2xl shadow-inner">
              <ShieldAlert className="h-6 w-6 text-amber-200" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Connect to Admin / Raise Concern</h2>
              <p className="text-amber-100 text-xs">Submit inquiries, feedback, or support requests</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-xl transition"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto">
          {submittedMessage ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-600 shadow-inner">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Concern Submitted!</h3>
              <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-4 text-amber-900 text-sm font-semibold leading-relaxed shadow-sm">
                <Sparkles className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                "{submittedMessage}"
              </div>
              <p className="text-xs text-gray-500 max-w-xs mx-auto">
                Your ticket has been logged into our administration portal. You will receive an in-app notification when an admin responds.
              </p>
              <button
                onClick={onClose}
                className="mt-4 w-full bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-white font-bold py-3 rounded-xl shadow transition"
              >
                Close Window
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-sm">
              <div className="bg-amber-50/70 p-3 rounded-2xl border border-amber-100 flex items-center gap-3 text-xs text-amber-900">
                <MessageSquare className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span>Our administration team reviews all submitted concerns promptly. You will be notified of updates.</span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-xs font-medium"
                >
                  <option value="General Concern">General Concern</option>
                  <option value="Cacao Farming Assistance">Cacao Farming Assistance</option>
                  <option value="Processing & Equipment Inquiry">Processing & Equipment Inquiry</option>
                  <option value="Directory Listing Request Support">Directory Listing Request Support</option>
                  <option value="Technical / Platform Feedback">Technical / Platform Feedback</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  required
                  placeholder="Brief summary of your concern..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Number (Optional)</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="e.g. 09171234567"
                  value={contactNumber}
                  onChange={handleContactNumberChange}
                  className={`w-full px-3.5 py-2.5 border rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-xs ${
                    contactNumberError ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {contactNumberError && (
                  <p className="mt-1 text-[11px] text-red-500 font-medium">{contactNumberError}</p>
                )}
                <p className="mt-0.5 text-[10px] text-gray-400">Numbers only · exactly 11 digits (e.g. 09171234567)</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details & Description</label>
                <textarea
                  required
                  rows={4}
                  placeholder="Please describe your concern or feedback in detail..."
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-xs resize-none"
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold text-xs rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-white font-bold text-xs rounded-xl shadow flex items-center justify-center gap-2 disabled:opacity-50 transition"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" /> Submit Concern
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
